import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from '../features/admin/dashboard/AdminLayout';
import AdminDashboardHome from '../features/admin/dashboard/AdminDashboardHome';
import Login from '../features/auth/screens/Login';
import PrivateRoute from '../features/auth/components/PrivateRoute';
import MigrateMesas from '../legacy/tools/MigrateMesas';
import RestaurantDetailScreen from '../features/super-admin/screens/RestaurantDetailScreen';
import AdminMesasScreen from '../features/admin/mesas/AdminMesasScreen';
import QRCodesScreen from '../features/admin/mesas/QRCodesScreen';
import MesaSummaryScreen from '../features/admin/mesas/MesaSummaryScreen';
import MesaQRCodeScreen from '../features/admin/mesas/MesaQRCodeScreen';
import MesaTransactionsScreen from '../features/admin/mesas/MesaTransactionsScreen';
import SettingScreen from '../features/admin/settings/SettingScreen';
import POSConfigScreen from '../features/admin/settings/POSConfigScreen';
import PagosScreen from '../features/admin/pagos/PagosScreen';
import SuperAdminLayout from '../features/super-admin/SuperAdminLayout';
import SuperAdminDashboard from '../features/super-admin/SuperAdminDashboard';
import CreateRestaurantScreen from '../features/super-admin/screens/CreateRestaurantScreen';
import RestaurantListScreen from '../features/super-admin/screens/RestaurantListScreen';
import MigrationToolScreen from '../features/super-admin/MigrationToolScreen';
import RestaurantStatisticsScreen from '../features/super-admin/screens/RestaurantStatisticsScreen';
import RestaurantTransactionsScreen from '../features/super-admin/screens/RestaurantTransactionsScreen';
import { useRestaurant } from '../providers/RestaurantContext';
import { UserRole } from '../types/restaurant';

const getDefaultRouteForRole = (role: UserRole | null): string => {
  switch (role) {
    case 'super_admin':
      return '/super-admin';
    case 'owner':
    case 'admin':
      return '/admin';
    case 'staff':
      return '/admin/mesas';
    default:
      return '/login';
  }
};

const AdminRoutes: React.FC = () => {
  const { userRole } = useRestaurant();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to={getDefaultRouteForRole(userRole)} replace />} />

      {/* Staff shortcut */}
      <Route path="/mesas" element={<Navigate to="/admin/mesas" replace />} />

      <Route
        path="/super-admin"
        element={
          <PrivateRoute allowedRoles={['super_admin']}>
            <SuperAdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<SuperAdminDashboard />} />
        <Route path="restaurants/:id" element={<RestaurantDetailScreen />} />
        <Route path="restaurants/:id/statistics" element={<RestaurantStatisticsScreen />} />
        <Route path="restaurants/:id/transactions" element={<RestaurantTransactionsScreen />} />
        <Route path="restaurants" element={<RestaurantListScreen />} />
        <Route path="create" element={<CreateRestaurantScreen />} />
        <Route path="migration" element={<MigrationToolScreen />} />
      </Route>

      <Route
        path="/admin"
        element={
          <PrivateRoute allowedRoles={['owner', 'admin', 'staff']}>
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<AdminDashboardHome />} />
        <Route path="settings" element={<SettingScreen />} />
        <Route path="pos-config" element={<POSConfigScreen />} />
        <Route path="mesas" element={<AdminMesasScreen />} />
        <Route path="mesa/:id/resumen" element={<MesaSummaryScreen />} />
        <Route path="mesa/:id/transactions" element={<MesaTransactionsScreen />} />
        <Route path="mesa/:id/qr" element={<MesaQRCodeScreen />} />
        <Route path="qrcodes" element={<QRCodesScreen />} />
        <Route path="pagos" element={<PagosScreen />} />
        <Route path="migrate" element={<MigrateMesas />} />
      </Route>

      <Route path="*" element={<Navigate to={getDefaultRouteForRole(userRole)} replace />} />
    </Routes>
  );
};

export default AdminRoutes;
